import { MelodyContour } from './melody/MelodyContour';
import { MotifBrain, MotifStrategy } from './melody/MotifBrain';
import { PhraseBuilder } from './melody/PhraseBuilder';
import { HarmonyUtils } from '../../utils/music/HarmonyUtils';
import { ThemeEngine } from './melody/ThemeEngine';
import { CadenceType } from './StructureEngine';
import { quantizeToGrid } from './melody/RhythmicMotif';

export interface MelodyResult {
    note: number;
    rhythmicOverride?: number; // Duration in beats
}

export class MelodyGenerator {
    private contour: MelodyContour;
    private motifBrain: MotifBrain;
    private phraseBuilder: PhraseBuilder;
    private themeEngine: ThemeEngine;

    // State needed for coordination
    private lastMelodyIndex: number | null = null;
    private lastBassNote: number | null = null;
    private lastSection: string = 'Verse';
    private themeReplayIndex: number = 0;

    // V9 MELODIC MEMORY: Buffer for theme callback replay
    private themeReplayBuffer: number[] = [];

    // AUDIT FIX 2.4: Leap-Step Rule State
    private lastInterval: number = 0;

    constructor() {
        this.contour = new MelodyContour();
        this.motifBrain = new MotifBrain();
        this.phraseBuilder = new PhraseBuilder();
        this.themeEngine = new ThemeEngine();
        this.reset();
    }

    public reset() {
        this.contour.reset();
        this.motifBrain.reset();
        this.phraseBuilder.reset();
        this.themeEngine.reset();
        this.lastMelodyIndex = null;
        this.lastBassNote = null;
        this.lastSection = 'Verse';
        this.themeReplayIndex = 0;
        // V9: Reset theme callback buffer
        this.themeReplayBuffer = [];
        this.lastInterval = 0;
    }

    public randomizeParams(mood: 'Happy' | 'Sad' | 'Energetic' | 'Calm' | 'Mystery') {
        this.contour.randomizeParams(mood);
    }

    public setMotifStrategy(strategy: MotifStrategy) {
        this.motifBrain.setStrategy(strategy);
    }

    public getNextMelodyNote(
        scaleNotes: number[],
        chordRootIndex: number,
        currentSection: 'Verse' | 'Chorus' | 'Bridge',
        mood: 'Happy' | 'Sad' | 'Energetic' | 'Calm' | 'Mystery' = 'Calm',
        currentBassNote: number | null = null,
        currentChordNotes: number[] = [],
        isStrongBeat: boolean = false,
        primaryVoiceNote: number | null = null,
        nextChordNotes: number[] = [],          // V10: Harmonic Anticipation
        progressionProgress: number = 0,        // V10: 0-1 how close to next chord
        cadenceType: CadenceType = 'none',      // P2 FIX: Cadence type from StructureEngine
        hookMode: 'record' | 'replay' | 'none' = 'none', // V13: Hook Architect
        hookMotif: number[] = [],                        // V13: External Hook Storage
        timeSinceLastNote: number = 0.5,                 // Phase 2: Duration since last note (seconds)
        currentBPM: number | null = 120                  // Phase 2: For quantization
    ): MelodyResult {
        this.contour.touchCount++;
        this.phraseBuilder.advance();

        // Capture starting index for Hook Recording
        const startingIndex = this.lastMelodyIndex;

        // P2 FIX: Update phrase position for goal-directed contour
        const phrasePosition = this.phraseBuilder.getPhrasePosition();
        this.contour.updatePhrasePosition(phrasePosition);

        // 1. CONTRARY MOTION (P2: Only if not forcing register correction)
        this.contour.updateContraryMotion(currentBassNote, this.lastBassNote);
        if (currentBassNote !== null) this.lastBassNote = currentBassNote;

        // P2 FIX: Update register position for bounds checking
        if (this.lastMelodyIndex !== null) {
            this.contour.updateRegisterPosition(this.lastMelodyIndex, scaleNotes.length);
        }

        // --- THEME ENGINE: SECTION MANAGEMENT ---
        if (currentSection !== this.lastSection) {
            // Commit hook for the previous section
            this.themeEngine.commitHook(this.lastSection);
            this.lastSection = currentSection;
            this.themeReplayIndex = 0;

            // V9 MELODIC MEMORY: Check for theme callback
            const themeCallback = this.themeEngine.getThemeCallback(currentSection);
            if (themeCallback && themeCallback.length > 0) {
                this.themeReplayBuffer = themeCallback;
                this.themeReplayIndex = 0;
                if (process.env.NODE_ENV === 'development') console.log(`[MelodyGenerator] Theme Callback triggered for ${currentSection}`);
            }

            // P2 FIX: Signal new phrase to contour for inter-phrase contrast
            this.contour.onPhraseStart(this.phraseBuilder.lastPhraseEndedHigh());
        }

        const pool = scaleNotes.slice(
            Math.max(0, chordRootIndex - 3),
            Math.min(scaleNotes.length, chordRootIndex + 15)
        );

        if (pool.length === 0) return { note: scaleNotes[Math.floor(scaleNotes.length / 2)] };

        // --- V13: HOOK REPLAY INTERCEPT ---
        // If replaying, we bypass all generation logic and strictly follow the recorded intervals.
        if (hookMode === 'replay' && hookMotif.length > 0 && startingIndex !== null) {
            // Get the specific interval from the buffer
            const interval = hookMotif[this.themeReplayIndex % hookMotif.length];
            this.themeReplayIndex++;

            // Calculate target index
            let targetIndex = startingIndex + interval;

            // Simple bounds clamping (preserve contour as much as possible)
            targetIndex = Math.max(0, Math.min(targetIndex, scaleNotes.length - 1));

            const replayNote = scaleNotes[targetIndex];
            this.updateLastIndex(scaleNotes, replayNote, hookMode, hookMotif, startingIndex, timeSinceLastNote, currentBPM);

            // Phase 2: Get Rhythmic Duration from Hook
            const hookData = this.themeEngine.getHook(currentSection);
            let rhythmicOverride = undefined;
            if (hookData && hookData.rhythm && hookData.rhythm.length > 0) {
                // Wrap index using modulo to loop rhythm with motif
                // Note: themeReplayIndex has already been incremented, so decrement for lookup or use previous?
                // Actually themeReplayIndex was just incremented at line 121 (in previous tool call? No, line 121 is inside the block).
                // Let's rely on modulo of hookMotif.length for buffer sync.
                // Current index logic: 
                // interval = buffer[idx]
                // rhythm = rhythmBuffer[idx]
                const rhythmIndex = (this.themeReplayIndex - 1) % hookData.rhythm.length;
                rhythmicOverride = hookData.rhythm[rhythmIndex];
            }

            // Sync phrase builder state
            this.phraseBuilder.trackNote(replayNote);
            return { note: replayNote, rhythmicOverride };
        }

        // --- COUNTERPOINT LOGIC (Voice Coalescence) ---
        // Allow "Decoration" (passing tones) on weak beats by occasionally skipping strict harmony
        // AUDIT FIX 4.0: Reduced probability from 0.6 to 0.2 to prevent "noodling".
        // Only allow decoration if the previous move was stepwise (smooth).
        // If we just leapt, we MUST land on a chord tone (Resolution).
        const isStepwiseMotion = Math.abs(this.lastInterval) <= 2;
        const shouldDecorate = !isStrongBeat && isStepwiseMotion && Math.random() < 0.2;

        if (primaryVoiceNote !== null && !shouldDecorate) {
            // If there's a primary note, try to harmonize with it (3rd or 6th)
            // 1. Find the primary note's index in the scale
            // We use simple interval logic: +/- 2 scale steps (3rd) or +/- 5 scale steps (6th)
            // But since we are generating a *Melody Note* (usually higher), let's say we want to be close?
            // "Conversational" usually means Call & Response (Alternating) OR Harmony (Simultaneous).
            // If simultaneous (this frame), we want Harmony.
            // Let's try to find a 3rd above or below.

            const primaryIndex = HarmonyUtils.findScaleIndexForDegree(scaleNotes, 0, 0); // Need actual index of note
            // Note: scaleNotes is just an array of MIDI.
            // Find closest instance of primaryVoiceNote in scaleNotes?
            // Actually, just find the closest note in 'pool' that forms a nice interval.

            let bestCounterNote = -1;
            let minDissonance = Infinity;

            for (const candidate of pool) {
                const interval = Math.abs(candidate - primaryVoiceNote) % 12;
                // Prefer 3rds (3, 4), 6ths (8, 9)
                // Penalize 2nds (1, 2), 7ths (10, 11)
                let score = 10;
                if (interval === 3 || interval === 4) score = 0; // 3rd
                if (interval === 8 || interval === 9) score = 1; // 6th
                if (interval === 7) score = 2; // 5th
                if (interval === 5) score = 3; // 4th
                if (interval === 0) score = 100; // Octave/Unison (Avoid unisons heavily to force stacking)
                if (interval === 1 || interval === 2) score = 20; // 2nd (Clash) (unless jazz)

                // STACKING BIAS: Prefer candidates ABOVE the primary note to build chords upwards
                if (candidate <= primaryVoiceNote) {
                    score += 5; // Penalty for going down or staying same
                }

                // Distance penalty (don't jump too far from last melody index if possible)
                if (this.lastMelodyIndex !== null && scaleNotes[this.lastMelodyIndex]) {
                    const dist = Math.abs(candidate - scaleNotes[this.lastMelodyIndex]);
                    score += dist * 0.1;
                }

                if (score < minDissonance) {
                    minDissonance = score;
                    bestCounterNote = candidate;
                }
            }

            if (bestCounterNote !== -1) {
                this.updateLastIndex(scaleNotes, bestCounterNote, hookMode, hookMotif, startingIndex, timeSinceLastNote, currentBPM);
                return { note: bestCounterNote };
            }
        }

        // Initialize start position logic
        if (this.lastMelodyIndex === null) {
            const centerOffset = Math.floor(Math.random() * 5) - 2;
            const startPos = Math.floor(pool.length / 2) + centerOffset;
            const note = pool[Math.max(0, Math.min(startPos, pool.length - 1))];
            this.lastMelodyIndex = scaleNotes.indexOf(note);
            return { note };
        }

        // 2. PHRASE RESOLUTION CHECK
        if (this.phraseBuilder.isPhraseEnding()) {
            // P2 FIX: Use cadential melody target based on cadence type
            const note = this.phraseBuilder.getResolutionTarget(scaleNotes, chordRootIndex, this.lastMelodyIndex, cadenceType);
            this.phraseBuilder.trackNote(note);
            this.updateLastIndex(scaleNotes, note, hookMode, hookMotif, startingIndex, timeSinceLastNote, currentBPM);
            return { note };
        }

        // P2 FIX: Check for breath point (natural pause suggestion)
        const isBreathPoint = this.phraseBuilder.getBreathPoint();
        if (isBreathPoint && this.lastMelodyIndex !== null) {
            // Hold the previous note (repeat it) for a breath
            const holdNote = scaleNotes[this.lastMelodyIndex] || scaleNotes[0];
            this.phraseBuilder.trackNote(holdNote);
            return { note: holdNote };
        }

        // P2 FIX: Check for climax point (phrase peak)
        if (this.contour.isAtClimaxPoint() && !this.contour.needsRegisterCorrection()) {
            const climaxStep = this.contour.getClimaxStep();
            // Apply climax step if not already at register extreme
            const lastNote = scaleNotes[this.lastMelodyIndex || 0] || pool[0];
            const lastPoolIndex = pool.indexOf(lastNote);
            let climaxTargetIndex = lastPoolIndex >= 0 ? lastPoolIndex + (climaxStep.step * climaxStep.direction) : Math.floor(pool.length / 2);
            climaxTargetIndex = Math.max(0, Math.min(climaxTargetIndex, pool.length - 1));
            const climaxNote = pool[climaxTargetIndex];
            this.phraseBuilder.trackNote(climaxNote);
            this.updateLastIndex(scaleNotes, climaxNote, hookMode, hookMotif, startingIndex, timeSinceLastNote, currentBPM);
            return { note: climaxNote };
        }

        // 3. MOTIF / STEP LOGIC
        let stepSize: number;
        let direction: number;

        // V9 MELODIC MEMORY: Check for theme callback replay FIRST (highest priority)
        if (this.themeReplayBuffer.length > 0 && this.themeReplayIndex < this.themeReplayBuffer.length) {
            const callbackStep = this.themeReplayBuffer[this.themeReplayIndex];
            stepSize = Math.abs(callbackStep);
            direction = Math.sign(callbackStep) || 1;
            this.themeReplayIndex++;

            // Clear buffer when exhausted
            if (this.themeReplayIndex >= this.themeReplayBuffer.length) {
                this.themeReplayBuffer = [];
                this.themeReplayIndex = 0;
            }
        }
        // CHECK FOR THEME REPLAY (existing hook logic)
        else {
            const activeHook = this.themeEngine.getHook(currentSection);
            // Only replay hooks in Chorus for now (The Hook!)
            const shouldReplay = activeHook && currentSection === 'Chorus';

            if (shouldReplay && activeHook) {
                // REPLAY HOOK
                const hookVal = activeHook.intervals[this.themeReplayIndex % activeHook.intervals.length];
                stepSize = Math.abs(hookVal);
                direction = Math.sign(hookVal) || 1;

                this.themeReplayIndex++;
            } else if (this.motifBrain.isReplaying()) {
                const replayStep = this.motifBrain.getReplayStep(currentSection);
                if (replayStep !== null) {
                    stepSize = Math.abs(replayStep);
                    direction = Math.sign(replayStep) || 1;
                } else {
                    // Fallback if replay ended mid-tick
                    stepSize = this.contour.getStepSize();
                    direction = this.contour.getDirection();
                }
                // If recording the theme, capture this replayed step too!
                if (this.themeEngine.shouldCapture(currentSection)) {
                    this.themeEngine.recordStep(stepSize * direction);
                }
            } else {
                stepSize = this.contour.getStepSize();
                direction = this.contour.getDirection();

                // --- V11 ENHANCEMENT: UNIVERSAL "PLEASING" LOGIC ---
                // 1. GAP FILL / LEAP-STEP RULE (Strict)
                // If last interval was a significant leap (> 4 semitones approx 2 scale steps), 
                // we MUST move in the opposite direction to "fill the gap".
                // Human ears crave balance.

                const lastIntervalAbs = Math.abs(this.lastInterval);

                if (lastIntervalAbs > 2) { // Leap of > 2 scale steps (approx a 4th or 5th)
                    // Force contrary motion
                    direction = -Math.sign(this.lastInterval) || 1;

                    // Damping: If the leap was Huge (> 4 steps, approx octave), force a step of 1.
                    if (lastIntervalAbs > 4) {
                        stepSize = 1;
                    } else {
                        // Otherwise allow a small step or skip (1 or 2)
                        stepSize = Math.random() > 0.7 ? 2 : 1;
                    }
                }

                // 2. PLEASURE OF EXPECTATION (Surprise Factor)
                // Occasionally (on weak beats) defy expectation with a "Color Note"
                // This creates a tension spike that releases dopamine upon resolution.

                // Only trigger surprise if: 
                // - We are stable (stepwise motion)
                // - Weak beat (not !isStrongBeat, checking caller...) 
                // - Random chance (~5-10%)

                const surpriseProb = 0.08;
                if (!isStrongBeat && lastIntervalAbs <= 1 && Math.random() < surpriseProb) {
                    // Force a "Surprise": 
                    // A larger leap (skip) or a direction change that wasn't expected?
                    // Let's do a "Tension Leap" -> Skip 2 scale steps (approx a 5th/6th)
                    // This creates a moment of "Oh, where are we going?"
                    stepSize = 3;
                    // Keep direction or flip? Flip creates more drama.
                    direction = -direction;
                }

                const signedStep = stepSize * direction;
                this.lastInterval = signedStep;

                this.motifBrain.recordStep(signedStep);

                // THEME RECORDING
                if (this.themeEngine.shouldCapture(currentSection)) {
                    this.themeEngine.recordStep(signedStep);
                }
            }
        }

        // 4. CALCULATE TARGET
        const lastNote = scaleNotes[this.lastMelodyIndex] || pool[0];
        const lastPoolIndex = pool.indexOf(lastNote);
        let targetPoolIndex = lastPoolIndex >= 0 ? lastPoolIndex : Math.floor(pool.length / 2);

        targetPoolIndex += (stepSize * direction);

        // Bounce Logic
        if (targetPoolIndex < 0) {
            targetPoolIndex = Math.abs(targetPoolIndex);
        } else if (targetPoolIndex >= pool.length) {
            targetPoolIndex = pool.length - 1 - (targetPoolIndex - pool.length);
        }
        targetPoolIndex = Math.max(0, Math.min(targetPoolIndex, pool.length - 1));

        let finalNote = pool[targetPoolIndex];

        // 5. TRITONE RESOLUTION (Improved)
        // AUDIT FIX 2.3: Check for tritone clashes against ANY chord tone, not just bass.
        // But allow tritones if they are part of the chord (e.g. 7th, #11).
        const isChordTone = currentChordNotes.length > 0 && currentChordNotes.some(n => (n % 12) === (finalNote % 12));

        if (!isChordTone && currentChordNotes.length > 0) {
            for (const cn of currentChordNotes) { // Check against all voices
                const interval = Math.abs((finalNote % 12) - (cn % 12));
                // 6 = Tritone
                if (interval === 6) {
                    // Clash found! resolving...
                    // Resolve contrary to the leap direction if possible, or just to nearest neighbor
                    const resolveDir = -direction || 1;
                    const resolvedIndex = targetPoolIndex + resolveDir;

                    if (resolvedIndex >= 0 && resolvedIndex < pool.length) {
                        finalNote = pool[resolvedIndex];
                        // If we resolved, stop checking other notes
                        break;
                    }
                }
            }
        } else if (currentBassNote !== null && !isChordTone) {
            // Fallback for bass tritone check if no chord notes
            const interval = Math.abs((finalNote % 12) - (currentBassNote % 12));
            if (interval === 6) {
                const resolveDir = -direction;
                const resolvedIndex = targetPoolIndex + resolveDir;
                if (resolvedIndex >= 0 && resolvedIndex < pool.length) {
                    finalNote = pool[resolvedIndex];
                }
            }
        }

        // 6. HARMONIC GRAVITY (P2 FIX: Apply on ALL beats, with varying strength)
        // Strong beats: 80% pull strength
        // Weak beats: 40% pull strength (allows some non-chord tones but biases toward chord)
        const gravityStrength = isStrongBeat ? 0.8 : 0.4;

        if (currentChordNotes.length > 0 && Math.random() < gravityStrength) {
            let bestMatch = finalNote;
            let minGravDist = Infinity;
            const gravityWell: number[] = [];

            currentChordNotes.forEach(cn => {
                const base = cn % 12;
                const targetOctave = Math.floor(finalNote / 12) * 12;
                gravityWell.push(targetOctave + base - 12);
                gravityWell.push(targetOctave + base);
                gravityWell.push(targetOctave + base + 12);
            });

            for (const wellNote of gravityWell) {
                const d = Math.abs(wellNote - finalNote);
                if (d < minGravDist) {
                    minGravDist = d;
                    bestMatch = wellNote;
                }
            }

            // P2 FIX: Tighter threshold on strong beats, looser on weak beats
            const pullThreshold = isStrongBeat ? 4 : 2;
            if (minGravDist <= pullThreshold) {
                finalNote = bestMatch;
            }
        }

        // 7. V10 HARMONIC ANTICIPATION (The Pull)
        // When we're more than halfway to the next chord, favor notes from upcoming chord
        if (progressionProgress > 0.5 && nextChordNotes.length > 0) {
            let bestAnticipation = finalNote;
            let minAnticipDist = Infinity;
            const anticipateWell: number[] = [];

            nextChordNotes.forEach(cn => {
                const base = cn % 12;
                const targetOctave = Math.floor(finalNote / 12) * 12;
                anticipateWell.push(targetOctave + base - 12);
                anticipateWell.push(targetOctave + base);
                anticipateWell.push(targetOctave + base + 12);
            });

            for (const wellNote of anticipateWell) {
                const d = Math.abs(wellNote - finalNote);
                if (d < minAnticipDist) {
                    minAnticipDist = d;
                    bestAnticipation = wellNote;
                }
            }

            // Only pull if the anticipation note is close (within 3 semitones)
            // Stronger pull when closer to chord change
            const pullThreshold = 3 + Math.floor((1 - progressionProgress) * 3);
            if (minAnticipDist <= pullThreshold) {
                finalNote = bestAnticipation;
            }
        }

        // P2 FIX: Track note for phrase peak calculation
        this.phraseBuilder.trackNote(finalNote);

        this.updateLastIndex(scaleNotes, finalNote, hookMode, hookMotif, startingIndex, timeSinceLastNote, currentBPM);
        return { note: finalNote };
    }

    private updateLastIndex(
        scaleNotes: number[],
        note: number,
        hookMode: 'record' | 'replay' | 'none',
        hookMotif: number[],
        startingIndex: number | null,
        timeSinceLastNote: number = 0.5,
        currentBPM: number | null = 120
    ) {
        let newIndex = scaleNotes.indexOf(note);
        if (newIndex === -1) {
            // Proxy logic for chromatic notes
            const closest = HarmonyUtils.findClosestInScale(scaleNotes, note);
            newIndex = scaleNotes.indexOf(closest || scaleNotes[0]);
        }
        this.lastMelodyIndex = newIndex;

        // V13: HOOK RECORDING logic
        if (hookMode === 'record' && startingIndex !== null && newIndex !== -1) {
            const interval = newIndex - startingIndex;
            hookMotif.push(interval);

            // Phase 2: Record Rhythm
            const durationBeats = quantizeToGrid(timeSinceLastNote, currentBPM);
            this.themeEngine.recordStep(interval, durationBeats);
        }
    }


}
